import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ContentChild, Input, TemplateRef } from '@angular/core';
import { CommonUtil } from '@libs/vc-core-lib';
import Highcharts, {
    LegendOptions,
    Options,
    PointClickEventObject,
    PointOptionsObject,
    SeriesPieOptions,
} from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { shuffle } from 'lodash-es';
import { ProgressSpinnerDirective } from 'vcp-common-ui-lib';
import { ChartWrapperComponent } from '../chart-wrapper/chart-wrapper.component';

@Component({
    selector: 'vc-pie-chart',
    imports: [CommonModule, HighchartsChartComponent, ChartWrapperComponent, ProgressSpinnerDirective],
    templateUrl: './pie-chart.component.html',
    styleUrl: './pie-chart.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PieChartComponent {
    highcharts: typeof Highcharts = Highcharts;

    private chartInstance?: Highcharts.Chart;

    readonly defaultChartColors = [
        'var(--primary-200)',
        'var(--primary-400)',
        'var(--primary-500)',
        'var(--primary-600)',
        'var(--primary-700)',
        'var(--primary-800)',
    ];

    options: Options = {
        chart: {
            type: 'pie',
            borderWidth: 0,
            events: {
                load: function (this: Highcharts.Chart) {
                    const legend = this.legend;

                    for (let i = 0, len = legend.allItems.length; i < len; i++) {
                        const point = legend.allItems[i] as LegendPoint;
                        const legendItem = point.legendItem;

                        if (!legendItem?.group) continue;

                        legendItem.group.on('mouseover', () => {
                            this.tooltip.refresh(point);
                        });

                        legendItem.group.on('mouseout', () => {
                            this.tooltip.hide();
                        });
                    }
                },
            },
        },
        title: {
            text: '',
        },
        plotOptions: {
            pie: {
                dataLabels: {
                    enabled: false,
                },
                showInLegend: true,
                innerSize: '50%',
                size: '100%',
                colors: shuffle(this.defaultChartColors),
            },
        },
        legend: {
            width: '40%',
            align: 'right',
            layout: 'vertical',
            verticalAlign: 'middle',
        },
        series: [
            {
                name: '',
                data: [],
                type: 'pie',
            },
        ],
        tooltip: {
            enabled: true,
            outside: true,
            format: '{point.name}: <b>{point.y}</b>',
            backgroundColor: CommonUtil.getRenderedColor('--surface-1'),
        },
    };

    @Input()
    contentHeight!: string;

    @Input()
    height!: string;

    @Input()
    title!: string;

    @Input()
    loading: boolean = false;

    @Input()
    chartWrapper: boolean = true;

    @Input()
    set data(value: Array<number | [string, number | null] | null | PointOptionsObject>) {
        this.options = { ...this.options, series: [{ data: value }] };
        this._updateChart();
    }

    @Input()
    set pointClick(value: (event: PointClickEventObject) => void) {
        this.options = {
            ...this.options,
            plotOptions: {
                ...(this.options.plotOptions ?? {}),
                series: {
                    ...(this.options.plotOptions?.series ?? {}),
                    point: {
                        ...this.options.plotOptions?.series?.point,
                        events: {
                            click: value,
                        },
                    },
                    cursor: value != undefined ? 'pointer' : 'default',
                },
            },
        };
        this._updateChart();
    }

    @Input()
    set tooltipFormat(value: string) {
        if (!value) {
            return;
        }
        this.options = {
            ...this.options,
            tooltip: {
                ...this.options.tooltip,
                format: value,
            },
        };
        this._updateChart();
    }

    // Full Series config
    @Input()
    set seriesPieOptions(value: SeriesPieOptions[]) {
        this.options = {
            ...this.options,
            series: value,
        };
        this._updateChart();
    }

    @Input()
    set colors(value: string[]) {
        this.options = {
            ...this.options,
            plotOptions: {
                ...this.options.plotOptions,
                pie: {
                    ...this.options.plotOptions?.pie,
                    colors: value,
                },
            },
        };
        this._updateChart();
    }

    @Input()
    set legendOptions(value: LegendOptions) {
        this.options = {
            ...this.options,
            legend: {
                ...this.options.legend,
                ...value,
            },
        };
        this._updateChart();
    }

    @ContentChild('chartWrapperEnd')
    chartWrapperEndTemplate!: TemplateRef<unknown>;

    onChartInstance(chart: Highcharts.Chart): void {
        this.chartInstance = chart;
        this._updateChart();
    }

    private _updateChart(): void {
        if (this.loading || !this.chartInstance?.renderer) {
            return;
        }

        this.chartInstance.update(this.options, true, true);
    }
}

interface LegendPoint extends Highcharts.Point {
    legendItem?: {
        group: Highcharts.SVGElement;
    };
}
